import { useState } from 'react';
import type { GuaranteedServiceOrgSkuEntry, GuaranteedServiceSkuEntry, PatchGuaranteedServiceRequest } from '@nadoutong/contracts';
import { useGuaranteedGuardConfigList, orgGuardsOf, skuGuardOf } from '../../api/guaranteedGuardConfig.ts';
import { skuNamespaceKey } from '../../components/SkuGroupPicker.tsx';
import { useT } from '../../i18n/index.ts';
import { InfoHint, Panel } from '../../ui/index.ts';
import { overlayFieldCount } from './guard/guardModel.ts';
import { GuardConfigDrawer } from './guard/GuardConfigDrawer.tsx';
import { OrgOverlayTable } from './OrgOverlayTable.tsx';
import { RowWriteStatus } from './RowWriteStatus.tsx';
import { SkuConfigCard } from './SkuConfigCard.tsx';
import { SKU_GRID } from './SkuRowParts.tsx';
import type { PatchOptions, RemovedWrite, WriteOutcome, WriteTarget } from './useGuaranteedWrites.ts';
import type { RowWriteStatus as RowStatus } from './writeStatus.ts';

function toggleSet(current: ReadonlySet<string>, key: string): ReadonlySet<string> {
  const next = new Set(current);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

export function ConfigCards({
  skuRows,
  orgRows,
  customerName,
  customerEmail,
  statusFor,
  removed,
  onPatch,
  onEditNote,
  onDelete,
}: {
  readonly skuRows: readonly GuaranteedServiceSkuEntry[];
  readonly orgRows: readonly GuaranteedServiceOrgSkuEntry[];
  readonly customerName: (orgId: string) => string;
  readonly customerEmail: (orgId: string) => string;
  readonly statusFor: (id: string) => RowStatus | undefined;
  /** Rows deleted on this page, with their Tokyo / Shanghai status (#2658). */
  readonly removed: readonly RemovedWrite[];
  readonly onPatch: (target: WriteTarget, body: PatchGuaranteedServiceRequest, options?: PatchOptions) => Promise<WriteOutcome>;
  readonly onEditNote: (id: string, note: string) => void;
  readonly onDelete: (id: string) => void;
}): JSX.Element {
  const t = useT();
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const guardList = useGuaranteedGuardConfigList();
  const [guardOpen, setGuardOpen] = useState<{ skuId: string; orgId: string | null } | null>(null);
  const guardSkuRow = guardOpen === null ? undefined : skuRows.find((row) => row.sku_id === guardOpen.skuId);
  const buckets = new Map<string, GuaranteedServiceSkuEntry[]>();
  for (const row of skuRows) {
    const key = skuNamespaceKey(row.sku_name);
    const existing = buckets.get(key);
    buckets.set(key, existing === undefined ? [row] : [...existing, row]);
  }
  const keys = [...buckets.keys()].sort((left, right) => {
    if (left === '') return 1;
    if (right === '') return -1;
    return left.localeCompare(right);
  });
  return (
    <Panel
      testId="guaranteed-config-card"
      caption={(
        <span className="inline-flex items-baseline gap-2">
          <span>{t('guaranteed.section.config')}</span>
          <span className="text-label font-normal text-paper-low">
            {t('guaranteed.config_count', { sku: String(skuRows.length), org: String(orgRows.length) })}
          </span>
        </span>
      )}
    >
      {guardList.isError ? (
        <p className="m-0 border-b border-bad-line px-3 py-2 text-label text-bad" data-testid="guaranteed-guard-load-failed" role="alert">
          {t('guaranteed.guard.load_failed')}
        </p>
      ) : null}
      {removed.length > 0 ? (
        <div className="flex flex-col gap-1 border-b border-line-soft px-3 py-2" data-testid="guaranteed-removed-status">
          {removed.map((entry) => (
            <div key={entry.id} className="flex flex-wrap items-baseline gap-2">
              <span className="text-label text-paper">{entry.name}</span>
              <RowWriteStatus status={entry.status} testId={`guaranteed-write-status-${entry.id}`} />
            </div>
          ))}
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <div className={`${SKU_GRID} min-w-[1120px] border-b border-line-soft px-3 py-2 text-label font-medium text-paper-low`}>
          <span>{t('guaranteed.col.sku')}</span>
          <span>{t('guaranteed.col.probe')}</span>
          <span>{t('guaranteed.col.chain')}</span>
          <span>{t('guaranteed.col.jev')}</span>
          <span>{t('guaranteed.col.fallback')}</span>
          <span className="inline-flex items-center gap-1">
            {t('guaranteed.col.guard')}
            <InfoHint
              label={t('guaranteed.guard.hint_label')}
              content={t('guaranteed.guard.hint')}
              testId="guaranteed-guard-hint"
            />
          </span>
          <span className="inline-flex items-center justify-end gap-1">
            {t('guaranteed.col.stats')}
            <InfoHint
              label={t('guaranteed.stats_hint_label')}
              content={t('guaranteed.stats_hint')}
              testId="guaranteed-entered-hint"
            />
          </span>
          <span />
        </div>
        {skuRows.length === 0 ? (
          <p className="m-0 px-3 py-3 text-dense text-paper-low">{t('guaranteed.empty')}</p>
        ) : keys.map((key) => {
          const token = key === '' ? 'other' : key;
          const open = !closed.has(token);
          const label = key === '' ? t('guaranteed.sku_group_other') : key;
          return (
            <div key={token} data-testid={`guaranteed-config-namespace-${token}`}>
              <button
                type="button"
                className="flex w-full items-center bg-raised/40 px-3 py-1.5 text-left text-label font-medium text-paper-mid"
                aria-expanded={open}
                onClick={() => setClosed((current) => toggleSet(current, token))}
              >
                {open ? '▾' : '▸'} {label}
              </button>
              {open ? (buckets.get(key) ?? []).map((row) => (
                <SkuConfigCard
                  key={row.id}
                  row={row}
                  status={statusFor(row.id)}
                  skuGuard={skuGuardOf(guardList.data, row.sku_id)}
                  orgGuards={orgGuardsOf(guardList.data, row.sku_id)}
                  onPatch={(body, options) => onPatch(row, body, options)}
                  onEditNote={() => onEditNote(row.id, row.note ?? '')}
                  onDelete={() => onDelete(row.id)}
                  guardDisabled={!guardList.isSuccess}
                  onOpenGuard={() => {
                    if (!guardList.isSuccess) return;
                    setGuardOpen({ skuId: row.sku_id, orgId: null });
                  }}
                />
              )) : null}
            </div>
          );
        })}
      </div>
      <div className="border-t-2 border-line">
        <div className="flex items-baseline gap-2 px-3 pt-2 text-label font-medium text-paper-mid">
          <span>{t('guaranteed.overlay.title')}</span>
          <span className="font-normal text-paper-low">{t('guaranteed.overlay.hint')}</span>
        </div>
        {orgRows.length === 0 ? (
          <p className="m-0 px-3 py-3 text-dense text-paper-low" data-testid="guaranteed-overlay-empty">
            {t('guaranteed.overlay.empty')}
          </p>
        ) : (
          <OrgOverlayTable
            rows={orgRows}
            customerName={customerName}
            customerEmail={customerEmail}
            statusFor={statusFor}
            overlayCount={(skuId, orgId) => overlayCountOf(guardList.data, skuId, orgId)}
            onPatch={onPatch}
            onEditNote={onEditNote}
            onDelete={onDelete}
            guardDisabled={!guardList.isSuccess}
            onOpenGuard={(skuId, orgId) => {
              if (!guardList.isSuccess) return;
              setGuardOpen({ skuId, orgId });
            }}
          />
        )}
      </div>
      {guardSkuRow !== undefined ? (
        <GuardConfigDrawer
          skuRow={guardSkuRow}
          skuGuard={skuGuardOf(guardList.data, guardSkuRow.sku_id)}
          orgGuards={orgGuardsOf(guardList.data, guardSkuRow.sku_id)}
          orgServiceRows={orgRows.filter((row) => row.sku_id === guardSkuRow.sku_id)}
          customerName={customerName}
          initialOrgId={guardOpen?.orgId ?? null}
          writeGate={guardList.data?.write_gate ?? { open: false, reason: null, checked_at: null }}
          listReady={guardList.isSuccess}
          onClose={() => setGuardOpen(null)}
        />
      ) : null}
    </Panel>
  );
}

function overlayCountOf(
  list: ReturnType<typeof useGuaranteedGuardConfigList>['data'],
  skuId: string,
  orgId: string,
): number {
  const row = list?.org_sku.find((entry) => entry.sku_id === skuId && entry.org_id === orgId);
  if (row === undefined) return 0;
  return overlayFieldCount(row.policy);
}
